/** iPhone / iPad (including iPadOS-as-Mac and iPhone “desktop website”). */
export const isIosAuthHostFor = (input: {
  userAgent: string
  platform: string
  maxTouchPoints: number
}): boolean => {
  if (/iPhone|iPod|iPad/.test(input.userAgent)) return true
  return (
    (input.platform === 'MacIntel' || /Macintosh/.test(input.userAgent)) &&
    input.maxTouchPoints > 1
  )
}

export const isIosAuthHost = (): boolean => {
  if (typeof navigator === 'undefined') return false
  return isIosAuthHostFor({
    userAgent: navigator.userAgent || '',
    platform: navigator.platform || '',
    maxTouchPoints: navigator.maxTouchPoints || 0,
  })
}

/** iOS browsers that are not Chrome / Firefox / Edge — popup breaks after async Turnstile. */
const IOS_NON_WEBKIT_BROWSER_UA = /CriOS|FxiOS|EdgiOS|OPiOS|GSA\//

export const isIosWebKitBrowserFor = (userAgent: string, isIos: boolean): boolean => {
  if (!isIos) return false
  return !IOS_NON_WEBKIT_BROWSER_UA.test(userAgent)
}

export const isIosWebKitBrowser = (): boolean => {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent || ''
  return isIosWebKitBrowserFor(ua, isIosAuthHost())
}

/** Firebase Hosting preview channels: `{site}--{channel}-{hash}.web.app`. */
export const isHostingPreviewChannel = (hostname: string): boolean =>
  hostname.endsWith('.web.app') && hostname.includes('--')

/**
 * Origins that are safe as a Google redirect continue URL.
 * Preview channels and leftover `*.web.app` hosts are not — those made
 * Google show "The requested action was invalid".
 */
/** `VITE_FIREBASE_AUTH_DOMAIN` is sometimes pasted with a scheme — compare hostnames only. */
export const normalizeAuthDomainHost = (value: string): string => {
  const trimmed = value.trim()
  if (!trimmed) return ''
  try {
    if (trimmed.includes('://')) {
      return new URL(trimmed).hostname
    }
  } catch {
    /* ignore */
  }
  return trimmed.replace(/\/$/, '')
}

export const isRedirectSafeOrigin = (hostname: string, authDomainHost = ''): boolean => {
  const authHost = normalizeAuthDomainHost(authDomainHost)
  if (!hostname) return false
  if (isHostingPreviewChannel(hostname)) return false
  if (hostname === 'localhost' || hostname === '127.0.0.1') return true
  if (hostname.endsWith('.optictruckworks.com')) return true
  return Boolean(authHost) && hostname === authHost
}

/** Popup OAuth can fail on Firebase v10 / strict browsers — redirect works on the same host. */
export const shouldFallbackGooglePopupToRedirect = (
  errorCode: string | undefined,
  hostname: string,
  authDomainHost = '',
): boolean => {
  if (!errorCode || !isRedirectSafeOrigin(hostname, authDomainHost)) return false
  return (
    errorCode === 'auth/invalid-action' ||
    errorCode === 'auth/popup-blocked' ||
    errorCode === 'auth/popup-closed-by-user' ||
    errorCode === 'auth/cancelled-popup-request' ||
    errorCode === 'auth/operation-not-supported-in-this-environment' ||
    errorCode === 'auth/network-request-failed'
  )
}

export type GoogleRedirectInput = {
  protocol: string
  hostname: string
  isIos: boolean
  /** Safari on iPhone/iPad — not Chrome (CriOS) or other alternate iOS browsers. */
  isIosWebKitBrowser?: boolean
  standalone: boolean
  authDomainHost?: string
}

/**
 * Redirect only when the auth handler is same-site (custom auth domain = portal host),
 * or when the app is installed (PWA) where popups are unreliable.
 *
 * iOS Chrome can use popup after async Turnstile; Safari cannot (gesture is lost).
 * Safari uses redirect to `*.firebaseapp.com` on first-party portal hosts.
 */
export const shouldUseGoogleRedirectFor = (input: GoogleRedirectInput): boolean => {
  if (input.protocol !== 'https:') return false
  if (!isRedirectSafeOrigin(input.hostname, input.authDomainHost || '')) return false

  const authHost = normalizeAuthDomainHost(input.authDomainHost || '')
  const sameSiteAuthHandler = Boolean(authHost) && input.hostname === authHost

  if (input.standalone) return true
  if (input.isIos) {
    if (sameSiteAuthHandler) return true
    if (input.isIosWebKitBrowser) return true
    return false
  }
  return false
}

export const shouldUseGoogleRedirect = (): boolean => {
  if (typeof window === 'undefined') return false
  const standalone =
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  const isIos = isIosAuthHost()
  const userAgent = navigator.userAgent || ''
  return shouldUseGoogleRedirectFor({
    protocol: window.location.protocol,
    hostname: window.location.hostname,
    isIos,
    isIosWebKitBrowser: isIosWebKitBrowserFor(userAgent, isIos),
    standalone: Boolean(standalone),
    authDomainHost: normalizeAuthDomainHost(process.env.VITE_FIREBASE_AUTH_DOMAIN || ''),
  })
}
